import gsap from 'gsap';
import { getCssVarInPx } from '../utils/getCssVarInPx';
import clamp from '../utils/clamp';

const DEFAULT_INTERVAL = 5000;
const STORAGE_KEY = 'announcement-bar-dismissed-until';
const DISMISS_DURATION_MS = 60 * 60 * 1000;

export default class AnnouncementBar extends HTMLElement {
  private closeButton: HTMLButtonElement | null = null;
  private container: HTMLUListElement | null = null;
  private items: HTMLLIElement[] = [];

  private height: number = 0;
  private top: number = 0;
  private headerHeight: number = 0;

  private intervalTime: number = DEFAULT_INTERVAL;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private previousItemIndex: number = 0;
  private currentItemIndex: number = 0;

  private onClickBound = this.onClick.bind(this);
  private onResizeBound = this.onResize.bind(this);
  private onScrollBound = this.onScroll.bind(this);
  private onVisibilityChangeBound = this.onVisibilityChange.bind(this);

  private closeTl: gsap.core.Timeline | null = null;
  private slideTl: gsap.core.Timeline | null = null;

  connectedCallback(): void {
    this.closeButton = this.querySelector('[data-announcement-bar-ref="close-button"]');
    this.container = this.querySelector('[data-announcement-bar-ref="container"]');
    this.items = Array.from(this.querySelectorAll('[data-announcement-bar-item-ref="items"]')) as HTMLLIElement[];

    const intervalAttr = this.getAttribute('data-interval');
    this.intervalTime = intervalAttr ? parseInt(intervalAttr, 10) : DEFAULT_INTERVAL;
    if (Number.isNaN(this.intervalTime)) {
      this.intervalTime = DEFAULT_INTERVAL;
    }

    if (this.isDismissed()) {
      this.hideImmediately();
      window.addEventListener('resize', this.onResizeBound);
      window.addEventListener('scroll', this.onScrollBound);
      this.onResize();
      this.onScroll();
      return;
    }

    this.addEventListeners();
    this.startSlideshow();
    document.addEventListener('visibilitychange', this.onVisibilityChangeBound);
  }

  private isDismissed(): boolean {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;

      const dismissedUntil = parseInt(raw, 10);
      if (Number.isNaN(dismissedUntil)) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      if (Date.now() < dismissedUntil) return true;

      localStorage.removeItem(STORAGE_KEY);
      return false;
    } catch {
      return false;
    }
  }

  private setDismissed(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + DISMISS_DURATION_MS));
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }

  private hideImmediately(): void {
    this.style.height = '0';
    this.style.overflow = 'hidden';
    this.style.visibility = 'hidden';
    this.setAttribute('aria-hidden', 'true');
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onClickBound);
    }
    window.addEventListener('resize', this.onResizeBound);
    window.addEventListener('scroll', this.onScrollBound);
    this.onResize();
    this.onScroll();
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onClickBound);
    }
    window.removeEventListener('resize', this.onResizeBound);
    window.removeEventListener('scroll', this.onScrollBound);
    document.removeEventListener('visibilitychange', this.onVisibilityChangeBound);

    this.onResize();
    this.onScroll();
  }

  goToNextItem(): void {
    if (this.items.length <= 1) return;

    this.previousItemIndex = this.currentItemIndex;
    this.currentItemIndex = this.previousItemIndex + 1 < this.items.length ? this.previousItemIndex + 1 : 0;

    if (this.slideTl) {
      this.slideTl.kill();
    }

    this.slideTl = gsap.timeline();
    this.slideTl.to(this.items[this.previousItemIndex], { duration: 0.6, autoAlpha: 0, ease: 'power2.out' }, 0);
    this.slideTl.to(this.items[this.currentItemIndex], { duration: 0.6, autoAlpha: 1, ease: 'power2.out' }, 0.7);
  }

  startSlideshow(): void {
    if (this.items.length <= 1) return;

    this.stopSlideshowInterval();
    this.intervalId = setInterval(() => {
      this.goToNextItem();
    }, this.intervalTime);
  }

  stopSlideshowInterval(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  stopSlideshow(): void {
    this.stopSlideshowInterval();

    if (this.slideTl) {
      this.slideTl.kill();
      this.slideTl = null;
    }
  }

  onVisibilityChange(): void {
    if (document.hidden) {
      this.stopSlideshowInterval();
      return;
    }

    this.startSlideshow();
  }

  onClick(): void {
    this.close();
  }

  onResize(): void {
    this.headerHeight = getCssVarInPx('--header-height');

    const boundingRect = this.getBoundingClientRect();
    this.height = boundingRect.height;
    this.top = boundingRect.top + window.scrollY;
  }

  onScroll(): void {
    const announcementBarOffset = clamp(this.top + this.height - window.scrollY, 0, this.top + this.height);
    document.documentElement.style.setProperty('--header-offset', `${this.headerHeight + announcementBarOffset}px`);
  }

  close(): void {
    this.setDismissed();
    this.stopSlideshow();
    this.setAttribute('aria-hidden', 'true');

    if (this.closeTl) {
      this.closeTl.kill();
    }

    this.closeTl = gsap.timeline({
      onComplete: () => {
        this.onResize();
        this.onScroll();
      },
    });
    this.closeTl.to(
      this,
      {
        height: 0,
        duration: 0.6,
        ease: 'expo.out',
      },
      0,
    );
    this.closeTl.to(
      this.container,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power2.out',
      },
      0,
    );
  }

  disconnectedCallback(): void {
    this.stopSlideshow();
    this.removeEventListeners();
  }
}

customElements.define('announcement-bar', AnnouncementBar);
